import { styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import Column from 'components/Layout/Column';
import Row from 'components/Layout/Row';
import Avatar from 'further-ui/components/Avatar';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import TypographyButton from 'further-ui/components/TypographyButton';
import {
  numberToCurrencyString,
  numberToDisplayString,
  numberToPercentageString,
  pluralize,
} from 'further-ui/utils';
import { useState } from 'react';
import { CommitmentData } from '../../types';

const StyledRow = styled(Row)(({ theme }) => ({
  padding: `${theme.spacing(theme.spacingSizes.xs)} 0`,
}));

type Props = Pick<CommitmentData, 'currencySettings'> & {
  holdingsByClass: CommitmentData['realisedHoldings']['byClass'];
};

const RealisedHoldingsByClass: React.FC<Props> = ({
  currencySettings,
  holdingsByClass: { data, totals },
}) => {
  const [expanded, setExpanded] = useState(false);
  const { currencyLcy } = currencySettings;

  const columns: Array<TableColumn<(typeof data)[number]>> = [
    {
      label: 'Asset',
      key: 'assetName',
      paddingY: 0,
      weight: 1.5,
      render: ({ assetImage, assetName }) => (
        <StyledRow centered spacing="xs">
          <Avatar color="random" text={assetName} size="lg" src={assetImage} />
          <Typography variant="inherit">{assetName}</Typography>
        </StyledRow>
      ),
      footer: (
        <>
          Total from {data.length} {pluralize('class', data.length, 'classes')}
        </>
      ),
    },
    {
      label: 'Share class',
      key: 'shareClassName',
      weight: 1.5,
      render: ({ shareClassName }) => shareClassName,
    },
    {
      label: 'Realisation date',
      key: 'realisationDate',
      weight: 1,
      render: ({ realisationDate }) =>
        realisationDate.toLabel(undefined, 'MMM dd, yyyy'),
    },
    {
      label: `Invested capital ${currencyLcy}`,
      key: 'investedCapitalLcy',
      weight: 1.25,
      render: ({ investedCapitalLcy }) =>
        numberToCurrencyString(investedCapitalLcy, {
          currency: currencyLcy,
        }),
      footer: numberToCurrencyString(totals.investedCapitalLcy, {
        currency: currencyLcy,
      }),
    },
    {
      label: `Realisation value ${currencyLcy}`,
      key: 'realisationValueLcy',
      weight: 1.25,
      render: ({ realisationValueLcy }) =>
        numberToCurrencyString(realisationValueLcy, {
          currency: currencyLcy,
        }),
      footer: numberToCurrencyString(totals.realisationValueLcy, {
        currency: currencyLcy,
      }),
    },
    {
      label: 'Gross MOIC',
      key: 'grossMoic',
      weight: 0.75,
      render: ({ grossMoic }) => `${grossMoic}x`,
      footer: `${totals.grossMoic}x`,
    },
    {
      label: 'Gross IRR',
      key: 'grossIrr',
      weight: 0.75,
      render: ({ grossIrr }) =>
        numberToPercentageString(grossIrr, { accuracy: 2 }),
      footer: numberToPercentageString(totals.grossIrr, {
        accuracy: 2,
      }),
    },
    ...(expanded
      ? [
          {
            label: 'Investment date',
            key: 'investmentDate',
            width: 200,
            render: ({ investmentDate }) =>
              investmentDate.toLabel(undefined, 'MMM dd, yyyy'),
          },
          {
            label: 'Pro-rated units realised',
            key: 'proRatedUnitsRealised',
            width: 200,
            render: ({ proRatedUnitsRealised }) =>
              numberToDisplayString(proRatedUnitsRealised),
          },
        ]
      : []),
  ];

  return (
    <Column spacing="md">
      <Table
        scrollable={expanded}
        emptyMessage="No holdings"
        columns={columns}
        rows={data}
      />
      <Row justify="flex-end">
        <TypographyButton
          text={expanded ? 'Collapse table' : 'Expand table'}
          onClick={() => setExpanded((prev) => !prev)}
        />
      </Row>
    </Column>
  );
};

export default RealisedHoldingsByClass;
